import { z } from 'zod'

/**
 * Zod schemas for FmpAnswers - represents raw form answers from Find My Plan questionnaire
 */

// Interstitial schema
const interstitialSchema = z.enum(['deepDive', 'quick'])

// Address schema
const addressSchema = z
  .object({
    city: z.string().optional().default(''),
    state: z.string().optional().default(''),
    zipcode: z.string().default(''),
    enriched: z.boolean().optional(),
  })
  .nullable()

// Moving schema
const movingSchema = z.enum(['yes', 'no']).nullable()

// Home Info schema
const homeInfoSchema = z.object({
  homeSize: z.enum(['apartment', 'small', 'large']),
  numBedrooms: z.enum(['1', '2', '3', '4', '5+']),
  numBathrooms: z.enum(['1', '2', '3', '4', '5+']),
  // Optional: the side rail leaves a blank floor count off rather than guess
  // one, and the ranking request then omits its floors key.
  numFloors: z.enum(['1', '2', '3', '4+']).optional(),
  yearBuilt: z.enum([
    '1940-1959',
    '1960-1969',
    '1970-1979',
    '1980-1989',
    '1990-1999',
    '2000-2009',
    '2010-2019',
    '2020 or newer',
    'unknown',
  ]),
  // Set only by the side rail, from a usable square-footage entry, so an edit
  // reaches consumption-prediction as the shopper's figure rather than the
  // address lookup's. Digits only: the side rail strips the commas it accepts.
  squareFootage: z
    .string()
    .regex(/^[1-9]\d*$/)
    .optional(),
})

// Non-Texas Native schema
const nonTexasNativeSchema = z.enum(['yes', 'no', '']).nullable()

// Bill Data schema
const billDataItemSchema = z.object({
  usage: z.number(),
  billing: z.number().nullable().optional(),
  day: z.number().nullable().optional(),
  month: z.string(),
  year: z.number(),
  zipCode: z.string().optional(),
  planName: z.string().optional(),
})

// Monthly Energy schema
const monthlyEnergySchema = z.object({
  familySize: z.enum(['1', '2', '3', '4', '5+']),
  daysAWeekAtHome: z.union([z.enum(['0', '1-2', '3-4', '5-6', '7']), z.literal('')]).optional(),
  summerTemp: z.string().optional(),
  winterTemp: z.string().optional(),
})

// Household Features enum
const householdFeatureEnum = z.enum([
  'centralHeatingAndAirConditioning',
  'naturalGasHeating',
  'poolOrHotTub',
  'smartThermostat',
  'homeAutomation',
  'electricVehicle',
  'solarPanels',
])

// Energy Plan Features enum
const energyPlanFeatureEnum = z.enum([
  'predictableMonthlyBill',
  'lowestTotalYearlyCost',
  'topRatedService',
  'greenEnergy',
  'billCredits',
])

// Vehicle Charge Time schema
const vehicleChargeTimeSchema = z.enum(['night', 'day', 'varies'])

// Term Choice schema
const termChoiceSchema = z.enum(['12months', '24months', '36months', 'unsure'])

/**
 * Main QuestionnaireAnswers schema with all fields optional
 */
export const questionnaireAnswersSchema = z.object({
  interstitial: interstitialSchema.optional(),
  address: addressSchema,
  moving: movingSchema,
  email: z.string().email().optional(),
  homeInfo: homeInfoSchema.optional(),
  nonTexasNative: nonTexasNativeSchema.optional(),
  billData: z.array(billDataItemSchema).optional(),
  monthlyEnergy: monthlyEnergySchema.optional(),
  householdIncludes: z.union([z.array(householdFeatureEnum), z.literal('')]).optional(),
  vehicleChargeTime: vehicleChargeTimeSchema.optional(),
  energyPlanIncludes: z.union([z.array(energyPlanFeatureEnum), z.literal('')]).optional(),
  termChoice: z.union([termChoiceSchema, z.literal('')]).optional(),
  formId: z.string(),
})

/**
 * Inferred TypeScript types from Zod schemas
 */
export type Interstitial = z.infer<typeof interstitialSchema>
export type Address = z.infer<typeof addressSchema>
export type Moving = z.infer<typeof movingSchema>
export type HomeInfo = z.infer<typeof homeInfoSchema>
export type NonTexasNative = z.infer<typeof nonTexasNativeSchema>
export type BillDataItem = z.infer<typeof billDataItemSchema>
export type MonthlyEnergy = z.infer<typeof monthlyEnergySchema>
export type HouseholdFeature = z.infer<typeof householdFeatureEnum>
export type EnergyPlanFeature = z.infer<typeof energyPlanFeatureEnum>
export type VehicleChargeTime = z.infer<typeof vehicleChargeTimeSchema>
export type TermChoice = z.infer<typeof termChoiceSchema>
export type QuestionnaireAnswers = z.infer<typeof questionnaireAnswersSchema>
